/* eslint-disable indent, react/jsx-indent */ // TODO: Remove once linting setup is fixed

import moment from 'moment'
import PropTypes from 'prop-types'
import React, { useContext, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, Link } from 'react-router-dom'
import styled from 'styled-components/macro'

import {
  formatMileage,
  getMonthNameByIndex,
  getObjectFromQueryString,
} from 'utilities/utils'

import {
  CarFileStatusEnumLowerCase,
  CarFileStatusEnumUpperCase,
  CarFileViewTypeEnum,
  CarIdentityPlateSizes,
} from 'config/enums'

import Definition from 'components/atoms/definition'
import Icon from 'components/atoms/icon'
import Text from 'components/atoms/text'
import Tooltip from 'components/atoms/tooltip'
import CarBrandAndModel from 'components/molecules/car-brand-and-model'
import CarIdentityPlate from 'components/molecules/car-identity-plate'
import CarFileCardBids, {
  CarFileCardBidsLayoutType,
  defaultProps as carFileCardBidsDefaultProps,
  propTypes as carFileCardBidsPropTypes,
} from 'components/molecules/carfile-card-bids'
import CarFileCardContextMenu from 'components/molecules/carfile-card-context-menu-container'
import CarFileCardPricing from 'components/molecules/carfile-card-pricing'
import CarFileCardTitle from 'components/molecules/carfile-card-title'
import CarKeySpecs from 'components/molecules/car-key-specs'
import StockDaysBadge from 'components/molecules/stock-days-badge'
import MarktplaatsCarCard from './marktplaats-car-card'
import MarktplaatsViewOverlay from 'components/redux-containers/marktplaats-view-overlay-container'
import { ThemeContext } from 'styled-components'
import { useMediaLayout } from 'use-media'
import Checkbox from 'components/atoms/checkbox'
import SelectableGalleryImage from 'components/atoms/selectable-gallery-image'
import { DateFormats, formatRoundedPrice, isPrice } from 'utilities/format'
import AdvertisementCarCard from 'components/molecules/advertisement-car-card'
import { useLongPress } from 'use-long-press'
import { useDispatch } from 'react-redux'
import { openOverlay } from 'redux/actions/ui'

const StyledCarCardWrapper = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  height: 100%;
`

const Container = styled.div`
  width: 100%;
  height: 100%;
  box-shadow: ${({ theme }) => theme.shadows.bottom};
  background-color: ${({ theme, $isSelected }) =>
    $isSelected ? theme.colors.isolatedBackground : theme.colors.brandGolf};
  border: ${({ theme, $isSelected }) =>
    $isSelected && `1px solid ${theme.colors.brandCharlie}`};
  border-radius: ${({ theme }) =>
    theme.cardBorderRadius ? `${theme.cardBorderRadius}px` : '0'};
  display: flex;
  flex-direction: column;
  transition: background-color 120ms ease-in-out;

  /* 
   * Prevent default link behaviour.
   * This allows us to use long press to select a card instead of opening the default mobile pop-up for links (e.g. 'open in new tab').
  */
  -webkit-touch-callout: none;
  -webkit-tap-highlight-color: transparent;
  user-select: none;
`

const StyledWrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  position: relative;

  > * {
    margin: 0 ${({ theme }) => theme.sizings.lvl2};
  }
`

const StyledInnerContainer = styled(Link)`
  // flex: 1 0;
  display: flex;
  flex-direction: column;
  text-decoration: none;
  padding-top: ${({ theme }) => theme.sizings.lvl2};
  padding-bottom: ${({ theme }) => theme.sizings.lvl2};

  & > * {
    flex: 0 0 auto;

    &:last-child {
      justify-self: flex-end;
    }
  }
`

const StyledInnerContainerHeader = styled.div`
  position: relative;
  display: flex;
  align-items: flex-start;

  & > div > span:nth-child(1),
  & > div > span:nth-child(2) {
    padding-right: ${({ theme }) => theme.sizeByFactor(16)};
  }
`

const ActionsContainer = styled.div`
  margin: 0 ${({ theme }) => theme.sizings.lvl2}
    ${({ theme }) => theme.sizings.lvl2};

  & > *:not(:first-child) {
    margin-top: ${({ theme }) => theme.sizings.lvl1};
  }
`

const StyledSummary = styled(Link)`
  text-decoration: none;
  background-color: ${({ theme }) => theme.colors.brandFoxtrot};
  margin-bottom: ${({ theme }) => theme.sizings.lvl2};
  margin-top: auto;
  border-radius: ${({ theme }) =>
    theme.cardBorderRadius ? `${theme.cardBorderRadius}px` : '0'};
  overflow: ${({ theme }) => (theme.cardBorderRadius ? 'hidden' : 'visible')};
`

const StyledLabeledTexts = styled.div`
  display: flex;
`

const StyledDefinition = styled(Definition)`
  border-left: 1px solid ${({ theme }) => theme.colors.brandDelta};
  padding: 0 ${({ theme }) => theme.sizings.lvl2};
  margin: ${({ theme }) => theme.sizings.lvl2} 0;

  &:first-child {
    border-left: 0;
  }
`

const StyledMeta = styled.div`
  align-items: center;
  display: flex;
  padding-top: ${({ theme }) => theme.sizings.lvl2};
  margin-bottom: auto;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.sizings.lvl0};
`

const StyledMetaText = styled(Text)`
  white-space: nowrap;
  flex-shrink: 0;
`

const StandardContent = styled(Link)`
  && {
    margin: 0;
  }

  display: flex;
  flex: 1 0 auto;
  flex-direction: column;
  gap: ${({ theme }) => theme.sizeByFactor(1.5)};
  padding: ${({ theme }) => theme.sizings.lvl2};
  text-decoration: none;
`

const StandardHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${({ theme }) => theme.sizings.lvl2};
`

const StandardTitle = styled(CarFileCardTitle)`
  flex: 1 1 auto;
  min-width: 0;
`

const StandardPlate = styled(CarIdentityPlate)`
  flex-shrink: 0;
  margin-left: auto;
`

const StyledCarFileCardPricing = styled(CarFileCardPricing)`
  margin-top: auto;
`

const StandardBids = styled(CarFileCardBids)`
  padding: 0;
`

const StyledExpected = styled(Text)`
  align-items: center;
  border-top: 1px solid ${({ theme }) => theme.colors.brandDelta};
  display: flex;
  padding: ${({ theme }) => theme.sizings.lvl2};
`

const OwnerContainer = styled.div`
  display: flex;
  text-transform: capitalize;

  & > *:first-child {
    margin-right: 0.5em;
  }
`
const TopBlock = styled.div`
  position: relative;
  margin: 0;

  ${({ theme }) =>
    theme.cardBorderRadius &&
    `
      > a {
        display: block;
        border-top-left-radius: ${theme.cardBorderRadius}px;
        border-top-right-radius: ${theme.cardBorderRadius}px;
        overflow: hidden;
      }
    `}
`

const CarFileLabels = styled.ul`
  position: absolute;
  bottom: 20px;
  right: 20px;
  display: flex;
  flex-direction: row-reverse;

  > li {
    display: block;
    height: 2em;
    width: 2em;
    border-radius: 50%;
    padding: 0.25em;
    background-color: ${({ theme }) => theme.colors.sectionBackground};
    margin-left: 10px;
  }
`

const StyledCarIdentityPlate = styled(CarIdentityPlate)`
  position: absolute;
  top: 0;
  right: 0;
  margin-left: auto;
  flex-shrink: 0;
`

const StyledCarFileCardBids = styled(CarFileCardBids)`
  padding: 0 ${({ theme }) => theme.sizings.lvl2}
    ${({ theme }) => theme.sizings.lvl2};
`

const StyledCarFileCardBidsSummary = styled(CarFileCardBids)`
  padding: ${({ theme }) => theme.sizings.lvl2};
  padding-top: ${({ theme }) => theme.sizings.lvl1};
`

const CommentsIconWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
`

const CommentsBadge = styled.span`
  position: absolute;
  top: -8px;
  right: -8px;
  background-color: ${({ theme }) => theme.colors.actionsStandard};
  color: white;
  border-radius: 50%;
  min-width: ${({ theme }) => theme.sizings.lvl2};
  height: ${({ theme }) => theme.sizings.lvl2};
  font-size: ${({ theme }) => theme.sizeByFactor(1.25)};
  font-weight: bold;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 3px;
  line-height: 1;
`

const StyledMarktplaatsCarCard = styled(MarktplaatsCarCard)`
  margin: 0 ${({ theme }) => theme.sizings.lvl2}
    ${({ theme }) => theme.sizings.lvl2} ${({ theme }) => theme.sizings.lvl2};
`

const StyledAdvertisementCarCard = styled(AdvertisementCarCard)`
  margin: 0 ${({ theme }) => theme.sizings.lvl2}
    ${({ theme }) => theme.sizings.lvl2} ${({ theme }) => theme.sizings.lvl2};
`

function CarfileCard({
  carId,
  actions,
  appraisal,
  brand,
  createdExternally,
  expected,
  image,
  license,
  vin,
  km,
  link,
  location,
  margin,
  model,
  month,
  owner,
  part1,
  part2,
  reportingCode,
  type,
  hideSummary,
  sellingPrice,
  exportPrice,
  priceEvaluation,
  exportPriceInclVat,
  onPriceEvaluationInfoClick,
  status,
  staytime,
  year,
  publishedOnline,
  cleaningOrderIsIssued,
  hasRemarks,
  remarksCount,
  isCarShare,
  carShareBids,
  isSelected,
  onSelectChange,
  showCheckbox,
  hasSelectedCard,
  marktplaatsData,
  carFileViewType,
  advertisementData,
  itemData,
  ...restProps
}) {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const theme = useContext(ThemeContext)
  const isDesktop = useMediaLayout({ minWidth: theme.metrics.desktop })
  const [marktplaatsOverlayOpen, setMarktplaatsOverlayOpen] = useState(false)
  const [overlayOpen, setOverlayOpen] = useState(false)
  // const { dealerName: carLocation } = useDealerLocationName()
  const routeLocation = useLocation()
  const allParams = getObjectFromQueryString(routeLocation.search)
  const selectedViewType =
    allParams.view === CarFileViewTypeEnum.ADS
      ? CarFileViewTypeEnum.ADVERTISEMENT
      : allParams.view
  const salesView = selectedViewType === CarFileViewTypeEnum.SALES
  const marktplaatsView = selectedViewType === CarFileViewTypeEnum.MARKTPLAATS
  const marktplaatsStatus = marktplaatsData?.status
  const [currentMarktplaatsStatus, setCurrentMarktplaatsStatus] =
    useState(marktplaatsStatus)
  useEffect(() => {
    setCurrentMarktplaatsStatus(marktplaatsStatus)
  }, [marktplaatsStatus])
  const isPhone = useMediaLayout({ maxWidth: theme.metrics.phone - 1 })

  const handleMarktplaatsStatusChange = (newStatus) => {
    setCurrentMarktplaatsStatus(newStatus)
  }
  const advertisementView =
    selectedViewType === CarFileViewTypeEnum.ADVERTISEMENT
  const standardView = !salesView && !marktplaatsView && !advertisementView
  const isInProgress =
    status === CarFileStatusEnumLowerCase[CarFileStatusEnumUpperCase.lopend]
  const primaryPrice = isInProgress ? appraisal : sellingPrice
  const primaryPriceText = isPrice(primaryPrice)
    ? formatRoundedPrice(primaryPrice)
    : t('pricesMissing')
  const sellingPriceText = isPrice(sellingPrice)
    ? formatRoundedPrice(sellingPrice)
    : t('pricesMissing')
  const showStockDays =
    staytime !== null &&
    typeof staytime !== 'undefined' &&
    [
      CarFileStatusEnumLowerCase[CarFileStatusEnumUpperCase.binnen],
      CarFileStatusEnumLowerCase[CarFileStatusEnumUpperCase.verkocht],
      CarFileStatusEnumLowerCase[CarFileStatusEnumUpperCase.afgeleverd],
    ].includes(status)

  const handleToggleMarktplaatsOverlay = () => {
    setMarktplaatsOverlayOpen(!marktplaatsOverlayOpen)
  }

  const handleToggleOverlay = () => {
    setOverlayOpen(!overlayOpen)
  }

  const carLabel = useMemo(() => {
    // When it's a carShare car, we want to display a static translation string
    if (isCarShare) {
      return t('carShare.carFileStockOverviewLabel')
    }

    // When the passed 'status' is a string in the CarFileStatusEnum (V3 / coming from the backend), then display the translated string for this status
    if (Object.values(CarFileStatusEnumLowerCase).includes(status)) {
      return t(`carFileStatussesV3.${status}`)
    }

    // When the above logic doesn't match, display the string that either has been given to this component or null, which shows no label in the Gallery Image
    return status
  }, [t, isCarShare, status])

  /**
   * Prevent native context menu on long-press,
   * This allows us to use long press to select a card instead of opening the default mobile pop-up for links (e.g. 'open in new tab').
   */
  // This allows us to use long press to select a card instead of opening the default mobile pop-up for links (e.g. 'open in new tab').
  const handleContextMenu = (event) => {
    event.preventDefault()
  }

  const handleSelectCar = () => {
    onSelectChange && onSelectChange(carId, !isSelected)
  }

  const handleMobileLongPress = useLongPress(
    handleSelectCar,
    {
      threshold: 500,
    },
    [],
  )

  return (
    <StyledCarCardWrapper {...restProps}>
      {showCheckbox && !isDesktop && (
        <Checkbox
          type="checkbox"
          name={carId}
          checked={isSelected}
          onChange={(checked) =>
            onSelectChange && onSelectChange(carId, checked)
          }
          aria-label="select-car"
        />
      )}
      <Container
        {...(isPhone ? handleMobileLongPress() : null)}
        $isSelected={isPhone && isSelected}
        onContextMenu={handleContextMenu}
      >
        <StyledWrapper>
          <TopBlock>
            {/* Preventing accidental navigation to carfile page due to misclick when selecting cards by making the image act as a label to the checkbox */}
            <Link
              to={link}
              onClick={
                hasSelectedCard
                  ? (e) => {
                      e.preventDefault()
                      handleSelectCar()
                    }
                  : undefined
              }
            >
              <SelectableGalleryImage
                src={image}
                alt="Auto title"
                label={carLabel}
                ratio={16 / 9}
                blurredBackground
                isSelected={isSelected}
                onSelectChange={onSelectChange && onSelectChange}
                showCheckbox={showCheckbox}
                carId={carId}
              />
            </Link>

            <CarFileLabels $isDesktop={isDesktop}>
              {!isCarShare && (
                <>
                  <CarFileCardContextMenu
                    carId={carId}
                    reportingCode={reportingCode}
                    mileage={km}
                    license={license}
                    isCarShare={isCarShare}
                    itemData={itemData}
                  />
                  {cleaningOrderIsIssued && (
                    <li>
                      <Tooltip message={t('carFileCleaningOrderIsIssued')}>
                        <Icon
                          type="cleaning"
                          color="actionsStandard"
                          width="22"
                          height="22"
                        />
                      </Tooltip>
                    </li>
                  )}
                  {publishedOnline && (
                    <li>
                      <Tooltip message={t('carFilePublishedOnline')}>
                        <Icon
                          color="actionsStandard"
                          size="lg"
                          type="carFilePublishedOnline"
                        />
                      </Tooltip>
                    </li>
                  )}
                  <li
                    onClick={() =>
                      dispatch(
                        openOverlay('comments', {
                          data: { auto_id: carId },
                        }),
                      )
                    }
                  >
                    <Tooltip message={t('addComment')}>
                      <CommentsIconWrapper>
                        <Icon
                          color="actionsStandard"
                          size="lg"
                          type="carFileHasRemarks"
                        />
                        {remarksCount > 0 && (
                          <CommentsBadge>{remarksCount}</CommentsBadge>
                        )}
                      </CommentsIconWrapper>
                    </Tooltip>
                  </li>
                  {/* Only show the createdExternally icon shen createdExternally === 1
                it can also be 2 but this means that it was altered within UCC after
                importing it from the external source
                */}
                  {createdExternally === 1 && (
                    <li>
                      <Tooltip message={t('carFileCreatedExternally')}>
                        <Icon
                          color="actionsStandard"
                          size="lg"
                          type="carFileCreatedExternally"
                        />
                      </Tooltip>
                    </li>
                  )}
                </>
              )}
            </CarFileLabels>
          </TopBlock>
          {standardView ? (
            <StandardContent
              to={link}
              onClick={
                hasSelectedCard && isPhone
                  ? (e) => {
                      e.preventDefault()
                      handleSelectCar()
                    }
                  : undefined
              }
            >
              <StandardHeader>
                <StandardTitle brand={brand} model={model} type={type} />
                <StandardPlate
                  carFileId={carId}
                  licensePlate={license}
                  vin={vin}
                  size={CarIdentityPlateSizes.COMPACT}
                />
              </StandardHeader>
              <CarKeySpecs items={[formatMileage(km), year, location]} />
              {owner && (
                <OwnerContainer>
                  <StyledMetaText type="body" text={`${t('owner')}: `} />
                  <StyledMetaText type="body" weight={500} text={t(owner)} />
                </OwnerContainer>
              )}
              {showStockDays && <StockDaysBadge days={staytime} />}
              {!hideSummary && (
                <StyledCarFileCardPricing
                  primaryLabel={
                    isInProgress ? t('appraisal') : t('sellingPrice')
                  }
                  primaryValue={primaryPriceText}
                  margin={margin}
                  exportPrice={exportPrice}
                  priceEvaluation={priceEvaluation}
                  exportPriceInclVat={exportPriceInclVat}
                  onPriceEvaluationInfoClick={onPriceEvaluationInfoClick}
                >
                  {expected && (
                    <Text
                      text={`${t('inStockExpected')}: ${moment(expected).format(
                        DateFormats.YEAR_MONTH_DAY,
                      )}`}
                    />
                  )}
                  {!isCarShare && !!carShareBids?.requested && (
                    <StandardBids
                      carShareBids={carShareBids}
                      layoutType={CarFileCardBidsLayoutType.VERTICAL}
                      breakDeadline={false}
                    />
                  )}
                </StyledCarFileCardPricing>
              )}
            </StandardContent>
          ) : (
            <>
              <StyledInnerContainer
                to={link}
                onClick={
                  hasSelectedCard && isPhone
                    ? (e) => {
                        e.preventDefault()
                        handleSelectCar()
                      }
                    : undefined
                }
              >
                <StyledInnerContainerHeader>
                  <CarBrandAndModel
                    carBrand={brand}
                    carModel={model}
                    carType={type}
                    size="small"
                    data-test-e2e="car-brand-and-model"
                  />
                  <StyledCarIdentityPlate
                    carFileId={carId}
                    licensePlate={license}
                    vin={vin}
                    size="small"
                  />
                </StyledInnerContainerHeader>
                <StyledMeta>
                  {(marktplaatsView || advertisementView) && (
                    <>
                      <StyledMetaText
                        type="body"
                        weight={500}
                        text={sellingPriceText}
                      />
                      {typeof km !== 'undefined' && (
                        <StyledMetaText type="bodyLight" text="|" />
                      )}
                    </>
                  )}
                  {typeof km !== 'undefined' && (month || year) && (
                    <>
                      <StyledMetaText type="body" text={formatMileage(km)} />
                      <StyledMetaText type="bodyLight" text="|" />
                      <StyledMetaText
                        type="body"
                        text={`${t(getMonthNameByIndex(month))} ${year}`}
                      />
                    </>
                  )}
                  {typeof km !== 'undefined' && !(month || year) && (
                    <Text type="body" text={formatMileage(km)} />
                  )}
                  {location && (
                    <>
                      <StyledMetaText type="bodyLight" text="|" />
                      <StyledMetaText type="body" text={location} />
                    </>
                  )}
                  {owner && (
                    <OwnerContainer>
                      <StyledMetaText type="body" text={`${t('owner')}: `} />
                      <StyledMetaText
                        type="body"
                        weight={500}
                        text={t(owner)}
                      />
                    </OwnerContainer>
                  )}
                </StyledMeta>
              </StyledInnerContainer>
              {!hideSummary && !marktplaatsView && !advertisementView && (
                <StyledSummary
                  to={link}
                  onClick={
                    hasSelectedCard && isPhone
                      ? (e) => {
                          e.preventDefault()
                          handleSelectCar()
                        }
                      : undefined
                  }
                >
                  <StyledLabeledTexts>
                    {status !==
                      CarFileStatusEnumLowerCase[
                        CarFileStatusEnumUpperCase.lopend
                      ] && (
                      <StyledDefinition
                        title={t('sellingPrice')}
                        value={primaryPriceText}
                      />
                    )}
                    {status ===
                      CarFileStatusEnumLowerCase[
                        CarFileStatusEnumUpperCase.lopend
                      ] && (
                      <StyledDefinition
                        title={t('appraisal')}
                        value={primaryPriceText}
                      />
                    )}
                    {!salesView && !!margin && (
                      <StyledDefinition title={t('margin')} value={margin} />
                    )}
                    {part1 && (
                      <>
                        <StyledDefinition title={t('part1')} value={part1} />
                        <StyledDefinition title={t('part2')} value={part2} />
                      </>
                    )}
                  </StyledLabeledTexts>
                  {expected && !salesView && (
                    <StyledExpected
                      text={`${t('inStockExpected')}: ${moment(expected).format(
                        DateFormats.YEAR_MONTH_DAY,
                      )}`}
                    />
                  )}
                  {!isCarShare && !!carShareBids?.requested && (
                    <StyledCarFileCardBidsSummary
                      carShareBids={carShareBids}
                      layoutType={CarFileCardBidsLayoutType.VERTICAL}
                      breakDeadline={false}
                    />
                  )}
                </StyledSummary>
              )}
            </>
          )}
        </StyledWrapper>
        {isCarShare && !!carShareBids?.deadline_formatted && (
          <StyledCarFileCardBids
            link={link}
            carShareBids={carShareBids}
            layoutType={CarFileCardBidsLayoutType.VERTICAL}
            breakDeadline={false}
          />
        )}
        {marktplaatsView && (
          <>
            <StyledMarktplaatsCarCard
              status={currentMarktplaatsStatus}
              carId={carId}
              marktplaatsData={marktplaatsData}
              onClick={handleToggleMarktplaatsOverlay}
              onMarktplaatsStatusChange={handleMarktplaatsStatusChange}
            />
            {marktplaatsOverlayOpen && (
              <MarktplaatsViewOverlay
                onClose={handleToggleMarktplaatsOverlay}
                image={image}
                brand={brand}
                model={model}
                type={type}
                licensePlate={license}
                mileage={km}
                year={year}
                sellingPrice={sellingPrice}
                carId={carId}
                marktplaatsInsights={marktplaatsData}
                selectedViewType={selectedViewType}
              />
            )}
          </>
        )}
        {advertisementView && advertisementData && (
          <StyledAdvertisementCarCard
            advertisementData={advertisementData}
            onClick={handleToggleOverlay}
          />
        )}
        {overlayOpen && (
          <MarktplaatsViewOverlay
            onClose={handleToggleOverlay}
            image={image}
            brand={brand}
            model={model}
            type={type}
            licensePlate={license}
            mileage={km}
            year={year}
            sellingPrice={sellingPrice}
            carId={carId}
            marktplaatsInsights={marktplaatsData || {}}
            advertisementData={advertisementData || {}}
            selectedViewType={selectedViewType}
          />
        )}
        {actions && <ActionsContainer>{actions}</ActionsContainer>}
      </Container>
    </StyledCarCardWrapper>
  )
}

CarfileCard.propTypes = {
  actions: PropTypes.object,
  appraisal: PropTypes.number,
  brand: PropTypes.string,
  createdExternally: PropTypes.number,
  expected: PropTypes.string,
  carId: PropTypes.string.isRequired,
  image: PropTypes.string,
  license: PropTypes.string,
  vin: PropTypes.string,
  link: PropTypes.string.isRequired,
  km: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  month: PropTypes.string,
  margin: PropTypes.number,
  model: PropTypes.string,
  owner: PropTypes.string,
  part1: PropTypes.string,
  part2: PropTypes.string,
  reportingCode: PropTypes.string, // meldcode
  type: PropTypes.string,
  location: PropTypes.string,
  /**
   * optionally hide the summary block containing pricing info and standing/stay-time
   */
  hideSummary: PropTypes.bool,
  sellingPrice: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  exportPrice: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  /** A PriceEvaluationEnum value or the 1-based step number (1 = very good price) */
  priceEvaluation: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /** True when the export price includes VAT (exportprijs), false for exportprijs_exbtw */
  exportPriceInclVat: PropTypes.bool,
  onPriceEvaluationInfoClick: PropTypes.func,
  status: PropTypes.string,
  staytime: PropTypes.number,
  year: PropTypes.number,
  publishedOnline: PropTypes.bool,
  cleaningOrderIsIssued: PropTypes.bool,
  hasRemarks: PropTypes.bool,
  isCarShare: PropTypes.bool,
  isSelected: PropTypes.bool,
  onSelectChange: PropTypes.func,
  showCheckbox: PropTypes.bool,
  hasSelectedCard: PropTypes.bool,
  marktplaatsData: PropTypes.object,
  carFileViewType: PropTypes.string,
  advertisementData: PropTypes.object,
  itemData: PropTypes.object,
  ...carFileCardBidsPropTypes,
}

CarfileCard.defaultProps = {
  actions: null,
  appraisal: null,
  brand: '-',
  createdExternally: 0,
  expected: null,
  image: null,
  license: undefined,
  vin: undefined,
  location: undefined,
  km: 0,
  margin: null,
  month: null,
  model: '-',
  owner: null,
  part1: null,
  part2: null,
  reportingCode: null, // meldcode
  type: null,
  hideSummary: false,
  sellingPrice: null,
  exportPrice: null,
  priceEvaluation: null,
  exportPriceInclVat: false,
  onPriceEvaluationInfoClick: null,
  status: null,
  staytime: null,
  year: null,
  publishedOnline: false,
  cleaningOrderIsIssued: false,
  hasRemarks: false,
  isCarShare: false,
  isSelected: false,
  onSelectChange: null,
  showCheckbox: false,
  hasSelectedCard: false,
  marktplaatsData: null,
  carFileViewType: CarFileViewTypeEnum.STANDARD,
  advertisementData: null,
  itemData: null,
  ...carFileCardBidsDefaultProps,
}

export default CarfileCard
